import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEY } from './hooks';
import type { CreatePlanInput, DevelopmentPlan, UpdatePlanInput } from './plans.types';

const API = '/api/plan-navigation-v3';

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init?.body ? { ...init, headers: { 'Content-Type': 'application/json' } } : init);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? `Erreur ${res.status}`);
  }
  return res.json() as Promise<T>;
}

const plansKey = (projectId: string) => [...KEY, 'plans', projectId] as const;

export function useDevelopmentPlans(projectId: string) {
  return useQuery<DevelopmentPlan[]>({
    queryKey: plansKey(projectId),
    queryFn: () => request(`${API}/projects/${projectId}/plans`),
  });
}

export function useCreatePlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation<DevelopmentPlan, Error, CreatePlanInput>({
    mutationFn: (input) => request(`${API}/projects/${projectId}/plans`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: plansKey(projectId) });
      qc.invalidateQueries({ queryKey: [...KEY, 'projects'] });
    },
  });
}

export function useUpdatePlan(projectId: string) {
  const qc = useQueryClient();
  return useMutation<DevelopmentPlan, Error, { planId: string; patch: UpdatePlanInput }>({
    mutationFn: ({ planId, patch }) =>
      request(`${API}/projects/${projectId}/plans/${planId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: (plan) => {
      qc.setQueryData<DevelopmentPlan[]>(plansKey(projectId), (plans) =>
        plans ? [plan, ...plans.filter((p) => p.id !== plan.id)] : plans);
    },
  });
}
